import React from 'react';
import Box from '@mui/material/Box';
import { Chip, Paper, Typography } from '@mui/material';
import { useActiveVehicle } from 'containers/pages/CANpark/context/ActiveVehicleProvider';
import ViolationActions from './ViolationActions';
import NextStepsLabel from './NextStepsLabel';
import { differenceInHours, differenceInMilliseconds, format } from 'date-fns';
import humanizeDuration from 'humanize-duration';
import { WarningOutlined } from '@mui/icons-material';
import { getNextViolationTicketType } from 'containers/pages/CANpark/utils/getNextViolationTicketType';

const formatMaxStay = (minutes?: number): string => {
	if (!minutes || minutes <= 0) return '';
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	if (h && m) return `${h}h ${m}m`;
	if (h) return `${h}h`;
	return `${m}m`;
};

const QuickSummaryCard: React.FC<{}> = () => {
	const { parkingLocation, tickets, inWindowTickets, parkingAuthorization } =
		useActiveVehicle();
	if (!parkingLocation) return null;

	// Count and decision are window-only; `tickets` (full history) is only used
	// for the most-recent-ticket warning below.
	const decision = getNextViolationTicketType({
		tickets: inWindowTickets,
		parkingLocation,
	});
	const totalRecords = inWindowTickets.length;
	const isAllowedToPark = parkingAuthorization?.allowed;
	const violationCheckType = parkingLocation?.violationCheckType;
	const lookupPeriod = parkingLocation?.violationCheckWindowDays || 0;

	const lastTicketDate = tickets[0] ? tickets[0].date : null;
	const isLastTicketLessThan24Hours =
		lastTicketDate !== null &&
		differenceInHours(new Date(), lastTicketDate) < 24;

	return (
		<Paper
			variant="outlined"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				borderRadius: 2,
				p: 1.5,
				width: '100%',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					gap: 1,
					width: '100%',
				}}
			>
				<Typography textAlign={'left'} variant={'h4'} fontWeight={300}>
					{totalRecords}
				</Typography>
				<Typography
					textAlign={'left'}
					variant={'body1'}
					fontWeight={300}
					width={'100%'}
				>
					Records Found in{' '}
					<Typography component="span" fontStyle="italic">
						{violationCheckType === 'fixedMonthly' &&
							format(new Date(), 'MMMM yyyy')}
						{violationCheckType === 'rolling' && (
							<>
								the last {lookupPeriod}{' '}
								{lookupPeriod === 1 ? 'day' : 'days'}
							</>
						)}
						{violationCheckType === 'hourlyExpiry' && (
							<>
								the last 30 days (max stay{' '}
								{formatMaxStay(parkingLocation.maxStayMinutes)})
							</>
						)}
					</Typography>
				</Typography>
				<Box
					display={'flex'}
					width={'100%'}
					gap={1}
					alignItems={'left'}
					flexWrap={'wrap'}
				>
					<Typography
						textAlign={'left'}
						variant={'body1'}
						fontWeight={300}
					>
						Next steps:{' '}
						{isAllowedToPark && (
							<Typography
								component={'span'}
								color={'primary'}
								fontStyle={'italic'}
							>
								No action required
							</Typography>
						)}
						{!isAllowedToPark && (
							<NextStepsLabel
								decision={decision}
								parkingLocation={parkingLocation}
							/>
						)}
					</Typography>
					{isLastTicketLessThan24Hours && (
						<Chip
							icon={<WarningOutlined />}
							label={`Last violation was recorded ${humanizeDuration(
								differenceInMilliseconds(
									new Date(),
									lastTicketDate,
								),
								{
									round: true,
									units: ['h', 'm'],
								},
							)} ago`}
							color="info"
							size="small"
							sx={{ ml: 1 }}
						/>
					)}
				</Box>

				<ViolationActions
					parkingAuthorization={parkingAuthorization}
					decision={decision}
				/>
			</Box>
		</Paper>
	);
};

export default QuickSummaryCard;
